// frontend/src/pages/reports/PersonnelVerificationStatusReport.js
import React, { useEffect, useState } from 'react';
import {
  Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Box, Typography,
  TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel, Grid,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import ReportTemplate from '../../components/reports/ReportTemplate';
import { getPersonnelVerificationStatusReport } from '../../services/reportService';

const STATUS_COLORS = {
  'VERIFIED':          'success',
  'NOT YET VERIFIED':  'warning',
  'DID NOT APPEAR':    'error',
};

const PersonnelVerificationStatusReport = () => {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  
  const [search, setSearch]         = useState('');
  const [zoneFilter, setZoneFilter] = useState('ALL');
  const [commandFilter, setCommandFilter] = useState('ALL');
  const [statusFilter, setStatusFilter]   = useState('ALL');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const res = await getPersonnelVerificationStatusReport();
      setData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load report');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const allPersonnel = data?.personnel || [];
  const zones = ['ALL', ...Object.keys(data?.summary?.byZone || {})];
  const commands = ['ALL', ...Object.keys(data?.summary?.byCommand || {})];
  const statuses = ['ALL', ...Object.keys(data?.summary?.byStatus || {})];

  const personnel = allPersonnel.filter((p) => {
    const matchSearch = 
      p.fullName.toLowerCase().includes(search.toLowerCase()) ||
      p.serviceNumber.toLowerCase().includes(search.toLowerCase()) ||
      (p.ippisNumber || '').toLowerCase().includes(search.toLowerCase());
    const matchZone    = zoneFilter === 'ALL'    || p.zone === zoneFilter;
    const matchCommand = commandFilter === 'ALL' || `${p.commandCode} - ${p.commandName}` === commandFilter;
    const matchStatus  = statusFilter === 'ALL'  || p.status === statusFilter;
    return matchSearch && matchZone && matchCommand && matchStatus;
  });

  const summary = data?.summary;
  const summaryItems = summary ? [
    { label: 'Total Personnel',  value: summary.totalPersonnel.toLocaleString(), color: 'primary.main', bg: '#e3f2fd' },
    { label: 'Verified',         value: summary.verified.toLocaleString(),       color: 'success.main', bg: '#e8f5e9' },
    { label: 'Not Yet Verified', value: summary.notYetVerified.toLocaleString(), color: 'warning.main', bg: '#fff3e0' },
    { label: 'Did Not Appear',   value: summary.nonVerified.toLocaleString(),    color: 'error.main',   bg: '#ffebee' },
  ] : [];

  const exportCols = [
    { key: 'serviceNumber',            label: 'Service Number' },
    { key: 'ippisNumber',              label: 'IPPIS Number' },
    { key: 'fullName',                 label: 'Full Name' },
    { key: 'rank',                     label: 'Rank' },
    { key: 'grade',                    label: 'Grade' },
    { key: 'commandCode',              label: 'Command Code' },
    { key: 'commandName',              label: 'Command Name' },
    { key: 'zone',                     label: 'Zone' },
    { key: 'state',                    label: 'State' },
    { key: 'taxState',                 label: 'Tax State' },
    { key: 'status',                   label: 'Status' },
    { key: 'nonVerificationReason',    label: 'Non-Verification Reason' },
    { key: 'nonVerificationDetails',   label: 'Non-Verification Details' },
    { key: 'phoneNumber',              label: 'Phone Number' },
    { key: 'email',                    label: 'Email' },
    { key: 'bankName',                 label: 'Bank Name' },
    { key: 'accountNumber',            label: 'Account Number' },
    { key: 'pfaName',                  label: 'PFA Name' },
    { key: 'dateOfFirstAppointment',   label: 'Date of First Appointment' },
    { key: 'expectedRetirementDate',   label: 'Expected Retirement Date' },
  ];

  return (
    <ReportTemplate
      title="Complete Personnel Verification Status Report"
      subtitle={data?.exercise ? `${data.exercise.name} — ${data.exercise.year}` : ''}
      loading={loading}
      error={error}
      onRefresh={load}
      exportFileName="Personnel_Verification_Status_Report"
      exportData={allPersonnel}
      exportColumns={exportCols}
      summaryItems={summaryItems}
    >
      <Paper elevation={2} sx={{ p: 3 }}>
        
        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, SN, or IPPIS..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Zone</InputLabel>
              <Select value={zoneFilter} label="Zone" onChange={(e) => setZoneFilter(e.target.value)}>
                {zones.map((z) => <MenuItem key={z} value={z}>{z}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Command</InputLabel>
              <Select value={commandFilter} label="Command" onChange={(e) => setCommandFilter(e.target.value)}>
                {commands.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                {statuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Results summary */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Showing <strong>{personnel.length.toLocaleString()}</strong> of <strong>{allPersonnel.length.toLocaleString()}</strong> personnel
            {zoneFilter !== 'ALL' && ` • Zone: ${zoneFilter}`}
            {commandFilter !== 'ALL' && ` • Command: ${commandFilter}`}
            {statusFilter !== 'ALL' && ` • Status: ${statusFilter}`}
          </Typography>
        </Box>

        {/* Table */}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1400 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ minWidth: 50 }}><strong>#</strong></TableCell>
                <TableCell sx={{ minWidth: 120 }}><strong>Service No.</strong></TableCell>
                <TableCell sx={{ minWidth: 120 }}><strong>IPPIS No.</strong></TableCell>
                <TableCell sx={{ minWidth: 200 }}><strong>Full Name</strong></TableCell>
                <TableCell sx={{ minWidth: 80 }}><strong>Rank</strong></TableCell>
                <TableCell sx={{ minWidth: 100 }}><strong>Grade</strong></TableCell>
                <TableCell sx={{ minWidth: 100 }}><strong>Command</strong></TableCell>
                <TableCell sx={{ minWidth: 80 }}><strong>Zone</strong></TableCell>
                <TableCell sx={{ minWidth: 140 }}><strong>Status</strong></TableCell>
                <TableCell sx={{ minWidth: 180 }}><strong>Non-Verification Reason</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>Verified At</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>Verified By</strong></TableCell>
                <TableCell sx={{ minWidth: 250 }}><strong>Comptroller</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {personnel.map((p, idx) => (
                <TableRow key={p.serviceNumber} hover>
                  <TableCell sx={{ color: 'text.secondary' }}>{idx + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{p.serviceNumber}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{p.ippisNumber || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{p.fullName}</Typography>
                  </TableCell>
                  <TableCell>{p.rank}</TableCell>
                  <TableCell>{p.grade}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{p.commandCode}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.commandName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={p.zone} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={p.status}
                      size="small"
                      color={STATUS_COLORS[p.status] || 'default'}
                      sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell>
                    {p.nonVerificationReason !== '-' ? (
                      <Box>
                        <Chip label={p.nonVerificationReason.replace(/_/g, ' ')} size="small" color="error" sx={{ mb: 0.5 }} />
                        {p.nonVerificationDetails !== '-' && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {p.nonVerificationDetails}
                          </Typography>
                        )}
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {p.verifiedAt ? (
                      <Box>
                        <Typography variant="caption">{new Date(p.verifiedAt).toLocaleDateString('en-GB')}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {new Date(p.verifiedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                        {p.referenceNumber !== '-' && (
                          <Typography variant="caption" color="primary.main" display="block" fontFamily="monospace">
                            {p.referenceNumber}
                          </Typography>
                        )}
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {p.verifiedBy !== '-' ? (
                      <Box>
                        <Typography variant="caption">{p.verifiedBy}</Typography>
                        {p.verifiedByServiceNumber !== '-' && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {p.verifiedByServiceNumber}
                          </Typography>
                        )}
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                      {p.comptroller}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {personnel.length === 0 && (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No personnel found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

      </Paper>
    </ReportTemplate>
  );
};

export default PersonnelVerificationStatusReport;